/**
 * True Fans Event Tracking Library
 */

class TrueFansTracker {
  constructor(dashboardUrl = 'https://true-fans-dashboard-hrcqeja3dbhjh5hs.eastus-01.azurewebsites.net') {
    this.dashboardUrl = dashboardUrl;
    this.sessionId = this.generateSessionId();
    this.init();
  }

  generateSessionId() {
    let sessionId = localStorage.getItem('tf_session_id');
    if (!sessionId) {
      sessionId = 'tf_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('tf_session_id', sessionId);
    }
    return sessionId;
  }

  async sendEvent(eventType, data = {}) {
    const eventData = {
      event_type: eventType,
      session_id: this.sessionId,
      page_url: window.location.href,
      referrer_url: document.referrer,
      ...data,
      metadata: {
        timestamp: new Date().toISOString(),
        user_agent: navigator.userAgent,
        viewport_size: `${window.innerWidth}x${window.innerHeight}`,
        ...data.metadata
      }
    };

    try {
      const response = await fetch(`${this.dashboardUrl}/api/events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(eventData)
      });

      const result = await response.json();
      console.log('TrueFans Event:', eventType, result);
      return result;
    } catch (error) {
      console.error('TrueFans Tracking Error:', error);
      return { success: false, error: error.message };
    }
  }

  trackPageVisit() {
    const currentUrl = window.location.href;

    if (currentUrl.includes('web-pixels') ||
        currentUrl.includes('sandbox') ||
        currentUrl.includes('pixel')) {
      console.log('Skipping team visit tracking - in web pixel environment');
      return;
    }

    const teamPagePatterns = [
      '/team/',
      '/league/',
      "/the-fan",
      "true-fan"
    ];

    const isTeamPage = teamPagePatterns.some(pattern => currentUrl.includes(pattern));

    if (!isTeamPage) {
      console.log('Skipping team visit tracking - not a team page');
      return;
    }

    const path = window.location.pathname.toLowerCase();
    const referrer = document.referrer;
    let eventType = '';

    if (path.includes('/team/')) {
      eventType = 'page_visit_team';
      const teamSlug = path.split('/team/')[1]?.split('/')[0];
      if (teamSlug) {
        return this.sendEvent(eventType, {
          team_id: teamSlug,
          team_name: this.getTeamNameFromSlug(teamSlug),
          metadata: {
            source_page: this.detectTrueFansPage(referrer),
            came_from_tf: referrer.includes('truefans') || referrer.includes('/team/')
          }
        });
      }
    } else if (path.includes('/league/')) {
      eventType = 'page_visit_league';
      const leagueSlug = path.split('/league/')[1]?.split('/')[0];
      if (leagueSlug) {
        return this.sendEvent(eventType, {
          league_name: this.getTeamNameFromSlug(leagueSlug),
          metadata: {
            source_page: this.detectTrueFansPage(referrer),
            came_from_tf: referrer.includes('truefans') || referrer.includes('/team/')
          }
        });
      }
    } else if (path.includes('/products/')) {
      // Only track if coming from True Fans page
      if (referrer.includes('truefans') || referrer.includes('/team/')) {
        const productSlug = path.split('/products/')[1]?.split('/')[0];
        if (productSlug) {
          return this.sendEvent('page_visit_pdp_from_tf', {
            product_id: productSlug,
            metadata: {
              source_page: this.detectTrueFansPage(referrer),
              referrer_type: 'true_fans',
              came_from_tf: true
            }
          });
        }
      }
      return; // Don't track regular PDP visits
    } else if (path.includes('/collections/')) {
      // Only track if coming from True Fans page
      if (referrer.includes('truefans') || referrer.includes('/team/')) {
        const collectionSlug = path.split('/collections/')[1]?.split('/')[0];
        if (collectionSlug) {
          return this.sendEvent('page_visit_collection_from_tf', {
            collection_id: collectionSlug,
            metadata: {
              source_page: this.detectTrueFansPage(referrer),
              referrer_type: 'true_fans',
              came_from_tf: true
            }
          });
        }
      }
      return; // Don't track regular collection visits
    } else if (path.includes('/the-fan') || path.includes('true-fans')) {
      eventType = 'page_visit_true_fans';
    }

    if (eventType) {
      return this.sendEvent(eventType, {
        metadata: {
          source_page: this.detectTrueFansPage(referrer),
          came_from_tf: referrer.includes('truefans') || referrer.includes('/team/')
        }
      });
    }
    return null
  }

  // Track team actions

  // source: 'the-fan' | 'plp' | 'pdp' — surface the action originated from
  trackTeamAdded(teamName, teamSlug, league, source) {
    const payload = {
      team_name: teamName,
      team_id: teamSlug,
      league_name: league
    };
    if (source) payload.source = source;
    return this.sendEvent('team_added', payload);
  }

  trackTeamRemoved(teamName, teamSlug, league, source) {
    const payload = {
      team_name: teamName,
      team_id: teamSlug,
      league_name: league
    };
    if (source) payload.source = source;
    return this.sendEvent('team_removed', payload);
  }

  trackTeamRanked(teamName, teamSlug, oldRank, newRank, totalTeams) {
    return this.sendEvent('team_ranked', {
      team_name: teamName,
      team_id: teamSlug,
      metadata: {
        old_rank: oldRank,
        new_rank: newRank,
        position: newRank, // Added for dashboard median rank calculation
        total_teams: totalTeams
      }
    });
  }

  // Track commerce events
  trackAddToCart(productHandle, path, collectionId = null) {
    const data = {
      product_id: productHandle, // Changed to match database schema
      collection_id: collectionId, // Added collection support
      metadata: {}
    }

    if (path.includes('/team/')) {
      const teamSlug = path.split('/team/')[1]?.split('/')[0];
      if (teamSlug) {
        data.team_id = teamSlug,
          data.team_name = this.getTeamNameFromSlug(teamSlug)
      };
    } else if (path.includes('/league/')) {
      const leagueSlug = path.split('/league/')[1]?.split('/')[0];
      if (leagueSlug) {
        data.league_id = leagueSlug,
          data.league_name = this.getTeamNameFromSlug(leagueSlug)
      };
    } else {
      data.page = path
    }

    return this.sendEvent('product_add_to_cart', data);
  }

  // Track navigation from True Fans to PDP
  trackPDPVisitFromTrueFans(productId, fromPage = null, teamInfo = null) {
    const referrer = document.referrer;
    const data = {
      product_id: productId,
      metadata: {
        source_page: fromPage || this.detectTrueFansPage(referrer),
        referrer_type: 'true_fans',
        came_from_tf: referrer.includes('truefans') || referrer.includes('/team/')
      }
    };

    // Include team context if available
    if (teamInfo) {
      data.team_id = teamInfo.teamId;
      data.team_name = teamInfo.teamName;
      data.league_name = teamInfo.league;
    }

    return this.sendEvent('page_visit_pdp_from_tf', data);
  }

  // Helper to detect which TF page user came from
  detectTrueFansPage(referrerUrl) {
    if (!referrerUrl) return 'direct';
    if (referrerUrl.includes('/team/')) return 'team_page';
    if (referrerUrl.includes('/league/')) return 'league_page';
    if (referrerUrl.includes('truefans')) return 'truefans_index';
    return 'other';
  }

  // Track navigation from True Fans to Collection
  trackCollectionVisitFromTrueFans(collectionId, fromPage = null, teamInfo = null) {
    const referrer = document.referrer;
    const data = {
      collection_id: collectionId,
      metadata: {
        source_page: fromPage || this.detectTrueFansPage(referrer),
        referrer_type: 'true_fans',
        came_from_tf: referrer.includes('truefans') || referrer.includes('/team/')
      }
    };

    // Include team context if available
    if (teamInfo) {
      data.team_id = teamInfo.teamId;
      data.team_name = teamInfo.teamName;
      data.league_name = teamInfo.league;
    }

    return this.sendEvent('page_visit_collection_from_tf', data);
  }

  // Track account creation
  trackAccountCreated(registrationMethod = 'email') {
    return this.sendEvent('account_created', {
      metadata: {
        registration_method: registrationMethod,
        marketing_source: this.getMarketingSource(),
        newsletter_opted_in: false // Update based on your form
      }
    });
  }

  // Helper methods
  getTeamNameFromSlug(teamSlug) {
    return teamSlug.replaceAll('-', ' ').split(' ').map(w => w.split('').map((l, i) => i === 0 ? l.toUpperCase() : l).join('')).join(' ')
  }

  getMarketingSource() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('utm_source') ||
      urlParams.get('source') ||
      document.referrer ||
      'direct';
  }

  // Auto-initialize tracking
  init() {
    // Track page visit on load
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        setTimeout(() => this.trackPageVisit(), 1000);
      });
    } else {
      setTimeout(() => this.trackPageVisit(), 1000);
    }
  }
}
window.TrueFansTracker = new TrueFansTracker();